import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { listAnsweredQuestions, submitQuestion } from "@/lib/academy.functions";

export const Route = createFileRoute("/academy/ask")({
  head: () => ({
    meta: [
      { title: "Ask Currency Thoughts — One Family Question, Answered Weekly" },
      {
        name: "description",
        content:
          "Submit a family money question. We answer one every week. Free. No login required.",
      },
      { property: "og:title", content: "Ask Currency Thoughts" },
      {
        property: "og:description",
        content: "We answer one family money question every week. Free.",
      },
    ],
    links: [{ rel: "canonical", href: "/academy/ask" }],
  }),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["academy", "answered"],
      queryFn: () => listAnsweredQuestions(),
    });
  },
  component: AskPage,
});

const CATEGORIES = [
  "Protection & Insurance",
  "Retirement",
  "Estate & Legacy",
  "Family & Children",
  "Business Owners",
  "Tax",
  "Other",
];

function AskPage() {
  const { data: answered } = useSuspenseQuery({
    queryKey: ["academy", "answered"],
    queryFn: () => listAnsweredQuestions(),
  });
  const submit = useServerFn(submitQuestion);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (question.trim().length < 10) {
      toast.error("Please add a bit more detail to your question.");
      return;
    }
    setBusy(true);
    try {
      await submit({ data: { email, question, name: name || undefined, category } });
      toast.success(
        "Question received. We answer one a week — if yours is selected, we'll email you when it's published.",
      );
      setName("");
      setEmail("");
      setQuestion("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not submit.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-20">
        {/* Hero */}
        <section className="bg-navy-900 text-cream-50">
          <div className="mx-auto max-w-4xl px-5 py-20 md:px-10 md:py-24 text-center">
            <div className="flex justify-center">
              <Eyebrow color="cream">Ask Currency Thoughts</Eyebrow>
            </div>
            <h1 className="mt-6 font-display text-[44px] leading-[1.05] md:text-[64px]">
              Ask Currency Thoughts.
            </h1>
            <p className="mx-auto mt-6 max-w-xl font-sans text-[17px] leading-relaxed text-cream-50/80">
              We answer one family question every week. Free. No login required.
            </p>
          </div>
        </section>

        {/* Submission card */}
        <section className="bg-cream-50 py-16">
          <div className="mx-auto max-w-2xl px-5 md:px-10">
            <div
              className="bg-white p-8 shadow-[0_24px_50px_-30px_rgba(168,139,92,0.4)] md:p-10"
              style={{ borderRadius: 6 }}
            >
              <Eyebrow>Submit Your Question</Eyebrow>
              <h2 className="mt-4 font-display text-[28px] text-navy-900">
                What's on your family's mind?
              </h2>
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Field label="Your name (optional)">
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={120}
                      className="w-full border border-bronze-600/30 bg-white px-4 py-3 font-sans text-[14px] outline-none focus:border-bronze-600"
                    />
                  </Field>
                  <Field label="Email (required)">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      maxLength={255}
                      className="w-full border border-bronze-600/30 bg-white px-4 py-3 font-sans text-[14px] outline-none focus:border-bronze-600"
                    />
                  </Field>
                </div>
                <Field label="Category">
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full border border-bronze-600/30 bg-white px-4 py-3 font-sans text-[14px] outline-none focus:border-bronze-600"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </Field>
                <Field label={`Your question (${question.length}/500)`}>
                  <textarea
                    required
                    minLength={10}
                    maxLength={500}
                    rows={6}
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Type your question here..."
                    className="w-full border border-bronze-600/30 bg-white px-4 py-3 font-sans text-[14px] outline-none focus:border-bronze-600"
                  />
                </Field>
                <PrimaryButton type="submit" size="lg" disabled={busy} className="w-full">
                  {busy ? "Sending..." : "Submit your question"}
                </PrimaryButton>
                <p className="font-sans text-[11px] leading-relaxed text-charcoal-500">
                  By submitting, you agree we may publish an edited version of your question
                  anonymously. We never share your email.
                </p>
              </form>
            </div>
          </div>
        </section>

        {/* Recently Answered */}
        {answered.length > 0 && (
          <section className="bg-white py-16">
            <div className="mx-auto max-w-6xl px-5 md:px-10">
              <div className="text-center">
                <Eyebrow>Recently Answered</Eyebrow>
                <h2 className="mt-4 font-display text-[32px] text-navy-900 md:text-[40px]">
                  Real questions from real families.
                </h2>
              </div>
              <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {answered.map((q) => (
                  <Link
                    key={q.id}
                    to="/academy/ask/$id"
                    params={{ id: String(q.id) }}
                    className="group flex h-full flex-col border border-bronze-600/15 bg-cream-50 p-6 transition-all hover:border-bronze-600/50 hover:shadow-[0_12px_30px_-18px_rgba(168,139,92,0.45)]"
                    style={{ borderRadius: 4 }}
                  >
                    <p className="font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
                      Q&A · Answered
                    </p>
                    <h3 className="mt-3 font-display text-[18px] leading-snug text-navy-900">
                      {truncate((q.question ?? "").replace(/^\[.*?\]\n\n/s, ""), 100)}
                    </h3>
                    {q.answer && (
                      <p className="mt-3 font-sans text-[13px] leading-relaxed text-charcoal-500">
                        {truncate(q.answer, 140)}
                      </p>
                    )}
                    <span className="mt-auto pt-4 font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-bronze-600 transition-transform group-hover:translate-x-1">
                      Read full answer →
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-charcoal-500">
        {label}
      </span>
      {children}
    </label>
  );
}

function truncate(s: string, n: number) {
  return s.length > n ? s.slice(0, n).trim() + "…" : s;
}
